import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaFilePdf, FaHistory, FaRecycle, FaSearch, FaTimes, FaTrash, FaUndo, FaUsers } from 'react-icons/fa';
import api from '../../api';
import YoungApprenticeService from '../../services/YoungApprenticeService';
import { downloadYoungApprenticeTablePdf } from './youngApprenticeReportPdf';
import './YoungApprenticeCompanyTools.css';

const parseDate = (value, dateOnlyMode = false) => {
  if (!value) return null;
  const raw = String(value).trim();
  const candidate = dateOnlyMode && /^\d{4}-\d{2}-\d{2}/.test(raw)
    ? new Date(`${raw.slice(0, 10)}T00:00:00`)
    : new Date(raw);
  return Number.isNaN(candidate.getTime()) ? null : candidate;
};
const dateOnly = (value) => {
  const parsed = parseDate(value, true);
  return parsed ? parsed.toLocaleDateString('pt-BR') : '—';
};
const dateTime = (value) => {
  const parsed = parseDate(value);
  return parsed ? parsed.toLocaleString('pt-BR') : '—';
};
const brl = (cents) => (Number(cents || 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const statusLabel = (status) => ({ ACTIVE: 'Ativo', PLANNED: 'Planejado', ENDED: 'Encerrado', SENT: 'Enviado', FAILED: 'Falhou', RETRY: 'Nova tentativa', PROCESSING: 'Processando', PENDING: 'Pendente', QUEUED: 'Na fila' }[String(status || '').toUpperCase()] || status || '—');
const errorMessage = (error, fallback) => error?.response?.data?.error || error?.message || fallback;

function PortalModal({ title, onClose, children, actions }) {
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className="ya-company-tools__backdrop" role="dialog" aria-modal="true">
      <section className="ya-company-tools__modal">
        <header><h2>{title}</h2><button type="button" onClick={onClose}><FaTimes /> Fechar</button></header>
        <div className="ya-company-tools__body">{children}</div>
        {actions && <footer>{actions}</footer>}
      </section>
    </div>,
    document.body,
  );
}

export function YoungApprenticeCompanyTrashButton({ onChanged }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState('');
  const load = async () => {
    setLoading(true); setFeedback('');
    try { setRows(await YoungApprenticeService.listCompanyTrash()); }
    catch (error) { setFeedback(errorMessage(error, 'Não foi possível carregar a lixeira.')); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open]);
  const restore = async (row) => {
    if (!window.confirm(`Restaurar a empresa “${row.tradeName || row.corporateName}”?`)) return;
    try { await YoungApprenticeService.restoreCompany(row.id); await load(); onChanged?.(); }
    catch (error) { setFeedback(errorMessage(error, 'Não foi possível restaurar a empresa.')); }
  };
  return <>
    <button type="button" className="young-apprentice-button young-apprentice-button--secondary" onClick={() => setOpen(true)}><FaRecycle /> Lixeira</button>
    {open && <PortalModal title="Lixeira de empresas" onClose={() => setOpen(false)}>
      {feedback && <div className="ya-company-tools__feedback">{feedback}</div>}
      {loading ? <p>Carregando...</p> : rows.length ? <div className="ya-company-tools__list">{rows.map((row) => <article key={row.id}><div><strong>{row.tradeName || row.corporateName}</strong><small>{row.cnpj || 'CNPJ não informado'} · excluída em {dateTime(row.sourceMetadata?.trash?.deletedAt)}</small></div><button type="button" onClick={() => restore(row)}><FaUndo /> Restaurar</button></article>)}</div> : <p>Nenhuma empresa na lixeira.</p>}
    </PortalModal>}
  </>;
}

export default function YoungApprenticeCompanyTools({ company, onChanged, canManage = false }) {
 const [linkSearch,setLinkSearch]=useState('');
 const [linkStatus,setLinkStatus]=useState('ALL');
 const [linkPdfLoading,setLinkPdfLoading]=useState(false);
  const [panel, setPanel] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState('');

  const title = useMemo(() => company?.tradeName || company?.corporateName || 'Empresa', [company]);
  const filteredLinks = useMemo(() => {
    const term = linkSearch.trim().toLocaleLowerCase('pt-BR');
    return (data?.linked || []).filter((row) => {
      if (linkStatus !== 'ALL' && String(row.status || '').toUpperCase() !== linkStatus) return false;
      if (!term) return true;
      return [row.fullName, row.registrationNumber, row.apprenticeNumber, row.role]
        .some((value) => String(value || '').toLocaleLowerCase('pt-BR').includes(term));
    });
  }, [data, linkSearch, linkStatus]);
  const open = async (type) => {
    setPanel(type); setData(null); setFeedback(''); setLoading(true); if (type === 'links') { setLinkSearch(''); setLinkStatus('ALL'); }
    try {
      if (type === 'links') setData(await YoungApprenticeService.companyLinks(company.id));
      if (type === 'emails') setData(await YoungApprenticeService.companyInvoiceEmailHistory(company.id));
    } catch (error) { setFeedback(errorMessage(error, 'Não foi possível carregar os dados.')); }
    finally { setLoading(false); }
  };

  const remove = async () => {
    setLoading(true); setFeedback('');
    try {
      const links = await YoungApprenticeService.companyLinks(company.id);
      if (links.current?.length) {
        setData(links); setPanel('delete-linked'); return;
      }
      if (!window.confirm(`Confirma a exclusão da empresa “${title}”? Ela será enviada para a lixeira.`)) return;
      await YoungApprenticeService.deleteCompany(company.id);
      setPanel(null); onChanged?.();
    } catch (error) { setFeedback(errorMessage(error, 'Não foi possível excluir a empresa.')); setPanel('delete-linked'); }
    finally { setLoading(false); }
  };

  const unlinkAndDelete = async () => {
    const count = Number(data?.current?.length || 0);
    if (!window.confirm(`Esta empresa possui ${count} jovem(ns) vinculado(s). Ao continuar, o vínculo atual será encerrado, o campo Empresa ficará vazio para esses jovens e a alteração será enviada ao Conere. Deseja continuar?`)) return;
    setLoading(true); setFeedback('');
    try {
      await YoungApprenticeService.deleteCompany(company.id, { unlink: true });
      setPanel(null); onChanged?.();
    } catch (error) { setFeedback(errorMessage(error, 'Não foi possível desvincular os jovens e excluir a empresa.')); }
    finally { setLoading(false); }
  };

  const downloadLinksPdf = async () => {
    setLinkPdfLoading(true); setFeedback('');
    try {
      const organization = await api.get('/company').then((response) => response.data).catch(() => null);
      await downloadYoungApprenticeTablePdf({
        title: 'Vínculos da Empresa',
        subtitle: title,
        organization,
        rows: filteredLinks,
        columns: [
          { key: 'fullName', label: 'Jovem', value: (row) => row.fullName || '—', weight: 1.7 },
          { key: 'registrationNumber', label: 'Matrícula', value: (row) => row.registrationNumber || '—' },
          { key: 'apprenticeNumber', label: 'Nº aprendiz', value: (row) => row.apprenticeNumber || '—' },
          { key: 'status', label: 'Status', value: (row) => statusLabel(row.status) },
          { key: 'startDate', label: 'Início', value: (row) => dateOnly(row.startDate) },
          { key: 'endDate', label: 'Fim', value: (row) => dateOnly(row.endDate) },
        ],
        filters: [linkSearch.trim() ? `Busca: ${linkSearch.trim()}` : '', linkStatus !== 'ALL' ? `Status: ${statusLabel(linkStatus)}` : 'Todos os status'].filter(Boolean).join(' • '),
        filename: `vinculos-${String(title).normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.pdf`,
      });
    } catch (error) { setFeedback(errorMessage(error, 'Não foi possível gerar o PDF dos vínculos.')); }
    finally { setLinkPdfLoading(false); }
  };

  const close = () => { setPanel(null); setData(null); setFeedback(''); };
  return <>
    <div className="ya-company-tools__actions">
      <button type="button" title="Ver jovens vinculados" onClick={() => open('links')}><FaUsers /><span>Vínculos</span></button>
      <button type="button" title="Histórico de envio de faturas" onClick={() => open('emails')}><FaHistory /><span>Envios</span></button>
      {canManage && <button type="button" className="danger" title="Excluir empresa" onClick={remove}><FaTrash /><span>Excluir</span></button>}
    </div>

    {panel === 'links' && <PortalModal title={`Jovens vinculados — ${title}`} onClose={close}>
      {feedback && <div className="ya-company-tools__feedback">{feedback}</div>}
      {!loading && data?.linked?.length > 0 && <div className="ya-company-links-toolbar"><label><FaSearch /><input value={linkSearch} onChange={(e) => setLinkSearch(e.target.value)} placeholder="Filtrar por jovem, matrícula, nº aprendiz ou função" /></label><select value={linkStatus} onChange={(e) => setLinkStatus(e.target.value)}><option value="ALL">Todos os vínculos</option><option value="ACTIVE">Ativos</option><option value="PLANNED">Planejados</option><option value="ENDED">Encerrados</option></select><button type="button" onClick={downloadLinksPdf} disabled={linkPdfLoading}><FaFilePdf /> {linkPdfLoading ? 'Gerando...' : 'Gerar PDF'}</button></div>}
      {loading ? <p>Carregando...</p> : data?.linked?.length ? <div className="ya-company-tools__table"><table><thead><tr><th>Jovem</th><th>Matrícula</th><th>Nº aprendiz</th><th>Status</th><th>Início</th><th>Fim</th></tr></thead><tbody>{filteredLinks.length ? filteredLinks.map((row) => <tr key={row.employmentId}><td>{row.fullName}</td><td>{row.registrationNumber || '—'}</td><td>{row.apprenticeNumber || '—'}</td><td>{statusLabel(row.status)}</td><td>{dateOnly(row.startDate)}</td><td>{dateOnly(row.endDate)}</td></tr>) : <tr><td colSpan="6">Nenhum vínculo encontrado com os filtros informados.</td></tr>}</tbody></table></div> : <p>Nenhum jovem vinculado a esta empresa.</p>}
      {!loading && canManage && data?.current?.length > 0 && <div className="ya-company-tools__warning">Há {data.current.length} vínculo(s) atual(is). A exclusão da empresa só é permitida após desvincular esses jovens.</div>}
    </PortalModal>}

    {panel === 'emails' && <PortalModal title={`Histórico de envio de faturas — ${title}`} onClose={close}>
      {feedback && <div className="ya-company-tools__feedback">{feedback}</div>}
      {loading ? <p>Carregando...</p> : Array.isArray(data) && data.length ? <div className="ya-company-tools__table"><table><thead><tr><th>Data</th><th>Fatura</th><th>Destinatários</th><th>Status</th><th>Retorno</th></tr></thead><tbody>{data.map((row) => <tr key={row.id}><td>{dateTime(row.sentAt || row.createdAt)}</td><td>#{row.invoiceId} · {row.reference || '—'}<small>{brl(row.totalCents)}</small></td><td>{row.recipients || row.snapshot?.recipients?.join?.(', ') || '—'}</td><td><span className={`ya-company-tools__status ya-company-tools__status--${String(row.deliveryStatus || '').toLowerCase()}`}>{statusLabel(row.deliveryStatus)}</span></td><td>{row.deliveryError || row.smtpMessageId || (String(row.deliveryStatus).toUpperCase() === 'SENT' ? 'Enviado com sucesso' : 'Sem retorno final ainda')}</td></tr>)}</tbody></table></div> : <p>Nenhum envio de fatura registrado para esta empresa.</p>}
    </PortalModal>}

    {panel === 'delete-linked' && <PortalModal title={`Não é possível excluir “${title}” ainda`} onClose={close} actions={<button type="button" className="danger" disabled={loading} onClick={unlinkAndDelete}><FaTrash /> Desvincular jovens e enviar empresa para lixeira</button>}>
      {feedback && <div className="ya-company-tools__feedback">{feedback}</div>}
      <div className="ya-company-tools__warning"><strong>{Number(data?.current?.length || 0)} jovem(ns) possuem vínculo atual com esta empresa.</strong><br />Para excluir, o iBPM encerrará esses vínculos, deixará o campo Empresa em branco no cadastro atual dos jovens e enviará a atualização ao Conere.</div>
      {!!data?.current?.length && <ul>{data.current.map((row) => <li key={row.employmentId}>{row.fullName}</li>)}</ul>}
    </PortalModal>}
  </>;
}
